import { ChevronRightThin, PlusThin } from "assets/svg";
import styled, { css } from "styled-components";

import { FullSectionLoader, ListSection, ListSectionItemContainer, SearchInput, Text } from "uikit";
import { InputWrapper, Units } from "uikit/Input/styles";
import { Loader, LoaderContainer } from "uikit/Loader/styles";

const openWidth = "300px";
const openHeight = "300px";
const closedHeight = "44px";

export const Container = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;
  border-radius: 8px;
  padding: ${({ theme }) => theme.padding.p8};
  gap: ${({ theme }) => theme.padding.p8};
  width: 100%;
  min-width: 100%;
  height: ${closedHeight};
  transition: all 250ms linear;
  overflow: hidden;

  ${({ isOpen }) =>
    isOpen
      ? css`
          position: absolute;
          top: 0;
          min-width: ${openWidth};
          width: ${openWidth};
          height: fit-content;
          max-height: ${openHeight};
          align-items: flex-start;
          transition: all 250ms linear;
          background-color: ${({ theme }) => theme.colors.colorsNew.base.white};
          border: 1px solid ${({ theme }) => theme.colors.colorsNew.gray[100]};
          box-shadow: 0px 4px 13.2px -4px ${({ theme }) => theme.colors.colorsNew.base.black}15;
          z-index: 100;
        `
      : css`
          &:hover {
            cursor: pointer;
            background-color: ${({ theme }) => theme.colors.colorsNew.gray[100]};
          }
        `}
`;

export const ClosedContainerInner = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  ${({ isOpen }) =>
    isOpen &&
    css`
      margin-bottom: ${({ theme }) => theme.margin.m8};
    `}
`;

export const ClosedDropdownMainContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.padding.p8};
`;

export const IconContainer = styled.div`
  width: 28px;
  height: 28px;
  background-color: ${({ theme }) => theme.colors.colorsNew.success[100]};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ArrowIcon = styled(ChevronRightThin)`
  margin-left: ${({ theme }) => theme.margin.m12};
`;

export const CloseIconContainer = styled.div`
  svg {
    width: ${({ theme }) => theme.iconSize.small};
    height: ${({ theme }) => theme.iconSize.small};

    path {
      fill: ${({ theme }) => theme.colors.colorsNew.gray["500_main"]};
    }
  }

  &:hover {
    cursor: pointer;
  }
`;

export const DropdownContent = styled.div<{ isOpen: boolean }>`
  display: none;
  flex-direction: column;
  gap: ${({ theme }) => theme.padding.p8};
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  ${({ isOpen }) =>
    isOpen &&
    css`
      display: flex;
    `}

  &::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.colorsNew.gray[400]};
    border-radius: 12px;
  }

  //TODO:::For Firefox
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.colorsNew.gray[400]} transparent;
`;

export const StyledSearchInput = styled(SearchInput)`
  width: 100%;
  max-width: 340px;
  margin-right: 20px;

  ${InputWrapper} {
    height: 36px;

    ${Units} {
      min-width: 24px;
      width: 24px;
      height: 24px;
      height: 100%;
      margin-left: 8px;
      border: none;
      background-color: transparent;

      svg {
        width: 16px;
        height: 16px;
      }
    }

    input {
      padding-left: 8px;
      padding-right: 8px;
      height: 100%;
      font-size: ${({ theme }) => theme.size.caption};
      font-weight: ${({ theme }) => theme.fontWeight.medium};
      border: none;
    }
  }
`;

export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.padding.p8};
  width: 100%;
  margin-top: ${({ theme }) => theme.margin.m40};
  height: 80px;
`;

export const AddNewCompanyButtonIcon = styled(PlusThin)`
  path {
    stroke: ${({ theme }) => theme.colors.colorsNew.blue.blue};
  }
`;

export const ListContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const List = styled(ListSection)<{ isLoading: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: ${({ isLoading }) => (isLoading ? "150px" : "auto")};
  gap: ${({ theme }) => theme.padding.p8};
  width: 100%;
  gap: 4px 0;
`;

export const StyledFullSectionLoader = styled(FullSectionLoader)`
  min-height: 100px;
  align-items: start;

  ${LoaderContainer} {
    min-height: auto;
    ${Loader} {
      width: 44px;
      height: 44px;

      div {
        width: 44px;
        height: 44px;
        border-width: 3px;
      }
    }
  }
`;

export const CompanyListItem = styled(ListSectionItemContainer)<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  padding: 8px;

  ${({ isSelected }) =>
    isSelected
      ? css`
          cursor: default;
          p,
          span {
            color: ${({ theme }) => theme.colors.colorsNew.gray[400]};
          }
        `
      : css`
          &:hover {
            cursor: pointer;
            background-color: ${({ theme }) => theme.colors.colorsNew.gray[100]};
          }
        `}
`;

export const CompanyListItemSideContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.padding.p8};
`;

export const InvitedLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.colorsNew.warning["500_main"]};
  font-size: ${({ theme }) => theme.size.caption};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
`;

export const PinIconContainer = styled.div`
  position: relative;
  z-index: 100;
  width: 16px;
  height: 16px;
  cursor: pointer;
`;
